/**
 * Electron bridge — typed wrapper around the API exposed in preload.js.
 *
 * Safe to import from anywhere in the app. In the browser / mobile build
 * `window.electronAPI` is undefined, so every export becomes a no-op and
 * `isElectron` is `false`.
 */

export interface DesktopPrefs {
  notificationSound: boolean;
  autoLaunch: boolean;
}

export interface ElectronAPI {
  isElectron: true;
  platform: NodeJS.Platform;
  showWindow: () => Promise<void>;
  quit: () => Promise<void>;
  getVersion: () => Promise<string>;
  getPrefs: () => Promise<DesktopPrefs>;
  setPrefs: (partial: Partial<DesktopPrefs>) => Promise<void>;
  onPrefsChanged: (cb: (prefs: DesktopPrefs) => void) => () => void;
  showNotification: (opts: {
    title?: string;
    body?: string;
    silent?: boolean;
    conversationId?: string | null;
  }) => Promise<void>;
  onNotificationClick: (
    cb: (info: { conversationId: string }) => void,
  ) => () => void;
  onUpdateAvailable: (cb: (info: { version: string }) => void) => () => void;
  onUpdateDownloaded: (cb: (info: { version: string }) => void) => () => void;
  windowMinimize: () => Promise<void>;
  windowToggleMaximize: () => Promise<boolean>;
  windowClose: () => Promise<void>;
  windowIsMaximized: () => Promise<boolean>;
  onWindowMaximizedChange: (cb: (isMaximized: boolean) => void) => () => void;
}

declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}

export const isElectron: boolean =
  typeof window !== "undefined" && window.electronAPI?.isElectron === true;

export const desktop = window?.electronAPI;

// Desktop window-chrome flags. macOS keeps its native traffic-light buttons;
// every other desktop platform draws custom min/max/close controls in the
// renderer (see WindowTitlebar).
export const isMacDesktop: boolean = isElectron && desktop?.platform === "darwin";
export const usesCustomWindowControls: boolean =
  isElectron && desktop?.platform !== "darwin";

export function minimizeWindow(): void {
  desktop?.windowMinimize().catch(() => {});
}

export function toggleMaximizeWindow(): void {
  desktop?.windowToggleMaximize().catch(() => {});
}

export function closeWindow(): void {
  desktop?.windowClose().catch(() => {});
}

export async function showDesktopNotification(opts: {
  title?: string;
  body?: string;
  silent?: boolean;
  conversationId?: string | null;
}): Promise<void> {
  if (!desktop) return;
  try {
    await desktop.showNotification(opts);
  } catch {
    /* swallow — notifications are best-effort */
  }
}

export function onDesktopNotificationClick(
  cb: (info: { conversationId: string }) => void,
): () => void {
  if (!desktop) return () => {};
  return desktop.onNotificationClick(cb);
}

export async function getDesktopPrefs(): Promise<DesktopPrefs | null> {
  if (!desktop) return null;
  try {
    return await desktop.getPrefs();
  } catch {
    return null;
  }
}

export async function setDesktopPrefs(
  partial: Partial<DesktopPrefs>,
): Promise<void> {
  if (!desktop) return;
  try {
    await desktop.setPrefs(partial);
  } catch {
    /* no-op */
  }
}
